/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import axios from 'axios';

import { Form } from 'core/forms';
import { addToast, handleAxiosError, removeModal } from 'core/notifications';

import { AppHeader, NewItemFormButtons } from '@/exceed';

import getAxiosConfig from '../helpers/services/getAxiosConfig';
import getSelectedUsersCount from '../helpers/services/getSelectedUsersCount';

import useEnrolledUsers from '../hooks/useEnrolledUsers';

import getAddUserEnrollmentsSchema from '../schemas/getAddUserEnrollmentsSchema';

import Enrollments from './enrollments';

import styles from '../styles/addUserEnrollmentsModal.module.scss';

const enrollmentsFormInitialData = {
  due_on: null,
  status: 'in-progress',
  relationship_type: 'recommended',
  recommended_by: '',
  relationship_reason: '',
};

const AddUserEnrollmentsModal = ({
  courseId,
  courseTitle,
  resetBaseSelectedUsers,
  statuses,
  baseEnrolledUsers,
}) => {
  const [page, setPage] = useState(1);
  const [formState, setFormState] = useState({});
  const [enrollmentsFormData, setEnrollmentsFormData] = useState(enrollmentsFormInitialData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    enrolledUsersQuery,
    removeEnrolledUsersQuery,
    enrollmentsSelectedUsers,
    removeEnrollmentsSelectedUsers,
  } = useEnrolledUsers({
    courseId,
    isModalVariant: true,
  });

  useEffect(
    () => () => {
      // must be remove instead of reset to avoid updating state on an unmounted component
      removeEnrolledUsersQuery();
      removeEnrollmentsSelectedUsers();
    },
    [removeEnrolledUsersQuery, removeEnrollmentsSelectedUsers],
  );

  const getListHeaderButtons = () => [
    {
      color: 'brand',
      icon: 'fullArrowRight',
      onClick: () => setPage(2),
      size: 'small',
      text: i18n.t('enrollment-details'),
    },
  ];

  const getModalHeaderTitleText = () => {
    const selectedUsersCount = getSelectedUsersCount({ enrollmentsSelectedUsers });
    return selectedUsersCount > 0 && page === 2
      ? i18n.t('enroll-count-users-in-course', {
          count: selectedUsersCount.toLocaleString(document.documentElement.lang),
          course: courseTitle,
        })
      : i18n.t('enroll-users-in-course', { course: courseTitle });
  };

  const handleDataErrors = ({ response }) => {
    response.data?.errors?.forEach(({ message, source }) => {
      const field = formState[source.split('data.').pop()];

      if (field) {
        Object.assign(field, { error: message, hasError: true });
      }
    });

    setEnrollmentsFormData({ ...enrollmentsFormData }); // trigger form update
    addToast(i18n.t('form-errors'), 'warning');
  };

  const handleFormLoad = ({ formState: newState }) => {
    setFormState(newState);
  };

  const onHomeClick = () => {
    setPage(1);
    setEnrollmentsFormData(enrollmentsFormInitialData);
  };

  const onFormUpdate = ({ formState: newState, update }) => {
    setFormState(newState);
    setEnrollmentsFormData({ ...enrollmentsFormData, ...update });
  };

  const onEnrollUsersClick = async () => {
    const {
      data: { users, hasSelectedAllUsers },
    } = enrollmentsSelectedUsers;
    const {
      filters: {
        user: { query, group_ids = [] },
      },
    } = enrolledUsersQuery;

    const params = {
      course_id: Number(courseId),
      filters: {
        user: {
          ids: hasSelectedAllUsers ? [] : Object.keys(users).map(Number),
          group_ids: group_ids.map(Number),
          query,
        },
      },
    };

    const payload = { ...enrollmentsFormData, ...params };
    const axiosConfig = getAxiosConfig('create-enrollments', payload);
    let count;

    setIsSubmitting(true);

    try {
      ({
        data: {
          data: { count },
        },
      } = await axios(axiosConfig));
    } catch (error) {
      setIsSubmitting(false);

      return error.response?.status === 422 ? handleDataErrors(error) : handleAxiosError(error);
    }

    baseEnrolledUsers.refetch();
    setEnrollmentsFormData(enrollmentsFormInitialData);
    resetBaseSelectedUsers();
    addToast(i18n.t('successful-enrollments', { count }, 'success'));
    removeModal();
  };

  return (
    <div>
      <div>
        <AppHeader
          className={styles.header}
          homeOnClick={onHomeClick}
          homeText={i18n.t('enroll-users')}
          homeRoute="#"
          hasHomeLink={page === 2}
          title={getModalHeaderTitleText()}
        />
        {page === 1 && (
          <Enrollments
            courseId={courseId}
            hasFixedHeader
            isModalVariant
            listHeaderButtons={getListHeaderButtons()}
            statusOptions={statuses}
          />
        )}
      </div>
      {page === 2 && (
        <div className={styles.form}>
          <Form
            model={enrollmentsFormData}
            onLoad={handleFormLoad}
            onUpdate={onFormUpdate}
            schema={getAddUserEnrollmentsSchema({
              relationship_type: enrollmentsFormData.relationship_type,
              enrolledUsers: baseEnrolledUsers,
              enrollmentsSelectedUsers,
              statuses,
            })}
          />
          <NewItemFormButtons
            hasError={Object.values(formState).some(({ hasError }) => hasError)}
            isFullScreen
            isSubmitting={isSubmitting}
            onCancel={onHomeClick}
            onSubmit={onEnrollUsersClick}
            submitText={i18n.t('enroll-users')}
          />
        </div>
      )}
    </div>
  );
};

AddUserEnrollmentsModal.propTypes = {
  courseId: PropTypes.number.isRequired,
  courseTitle: PropTypes.string.isRequired,
  resetBaseSelectedUsers: PropTypes.func.isRequired,
  statuses: PropTypes.arrayOf(PropTypes.string).isRequired,
  baseEnrolledUsers: PropTypes.shape({}).isRequired,
};

export default AddUserEnrollmentsModal;
